import { memo, useState, useCallback, useEffect, useMemo } from 'react';
import { Handle, Position } from 'reactflow';
import { Banana, X, Loader2, CheckCircle, Play, Download, Eye, Plus, ImagePlus, RefreshCw, Search, Globe } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { blobToFile } from '../../utils/mediaUtils';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, useReconfigure,
} from './ReconfigureControls';
import MultiInputHandle from './MultiInputHandle';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import PromptField from '../studio/PromptField';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, textNeed } from '../../utils/nodeRequirements';

const MAX_IMAGES = 14;

const ASPECT_RATIOS = [
  { value: '', label: 'Auto' },
  { value: 'match_input_image', label: 'Igual input' },
  { value: '1:1', label: '1:1' },
  { value: '2:3', label: '2:3' },
  { value: '3:2', label: '3:2' },
  { value: '3:4', label: '3:4' },
  { value: '4:3', label: '4:3' },
  { value: '4:5', label: '4:5' },
  { value: '5:4', label: '5:4' },
  { value: '9:16', label: '9:16' },
  { value: '16:9', label: '16:9' },
  { value: '21:9', label: '21:9' },
  { value: '1:4', label: '1:4' },
  { value: '4:1', label: '4:1' },
  { value: '1:8', label: '1:8' },
  { value: '8:1', label: '8:1' },
];

const RESOLUTIONS = [
  { value: '', label: 'Auto' },
  { value: '512px', label: '512px' },
  { value: '1K', label: '1K' },
  { value: '2K', label: '2K' },
  { value: '4K', label: '4K' },
];

const OUTPUT_FORMATS = [
  { value: 'jpg', label: 'JPG' },
  { value: 'png', label: 'PNG' },
];

const MULTI_INPUT_ID = 'multiInput';

const Banana2ProNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [prompt, setPrompt] = useState(data.prompt || '');
  const [images, setImages] = useState(data.images || []);
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || '');
  const [resolution, setResolution] = useState(data.resolution || '');
  const [outputFormat, setOutputFormat] = useState(data.outputFormat || 'jpg');
  const [googleSearch, setGoogleSearch] = useState(!!data.googleSearch);
  const [imageSearch, setImageSearch] = useState(!!data.imageSearch);
  const [lastPrompt, setLastPrompt] = useState(data.lastPrompt || '');

  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const edges = useWorkflowStore((state) => state.edges);
  const nodes = useWorkflowStore((state) => state.nodes);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);

  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const { openImagePreview } = useMediaPreview();

  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';

  // All connections landing on the multi-input handle (same target handle id)
  const connectedInputs = useMemo(() => {
    const conns = edges.filter(e => e.target === id && e.targetHandle === MULTI_INPUT_ID);
    return conns
      .map(edge => {
        const src = nodes.find(n => n.id === edge.source);
        return src?.data?.hasOutput ? src.data : null;
      })
      .filter(Boolean);
  }, [edges, nodes, id]);
  const connectedCount = connectedInputs.length;

  useEffect(() => {
    updateNodeData(id, { prompt, images, aspectRatio, resolution, outputFormat, googleSearch, imageSearch });
  }, [prompt, aspectRatio, resolution, outputFormat, googleSearch, imageSearch]); // eslint-disable-line

  const handleAddImage = useCallback(() => {
    if (images.length + connectedCount >= MAX_IMAGES) return;
    setSourcePickerHandler((result) => {
      if (result.file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImages(prev => {
            const updated = [...prev, { file: result.file, preview: e.target.result, name: result.file.name }];
            updateNodeData(id, { images: updated });
            return updated;
          });
        };
        reader.readAsDataURL(result.file);
      } else if (result.url) {
        setImages(prev => {
          const updated = [...prev, { file: null, preview: result.url, name: result.fileName, fromGallery: true }];
          updateNodeData(id, { images: updated });
          return updated;
        });
      }
    });
    openSourcePicker(id, ['image'], 'image/jpeg,image/jpg,image/png,image/webp');
  }, [id, images.length, connectedCount, openSourcePicker, updateNodeData]);

  const removeImage = useCallback((index) => {
    setImages(prev => {
      const updated = prev.filter((_, i) => i !== index);
      updateNodeData(id, { images: updated });
      return updated;
    });
  }, [id, updateNodeData]);

  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => setChronometer((prev) => prev + 1), 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const fetchFileFromUrl = async (url, filename) => blobToFile(url, filename, 'image/jpeg');

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);

  const handleProcess = useCallback(async () => {
    if (!prompt.trim()) return false;
    resetExec();

    const allImageFiles = [];
    const directImageUrls = [];

    // Collect from connected handles
    for (const input of connectedInputs) {
      if (!input) continue;
      let file = input.file;
      if (!file) {
        const fileUrl = input.outputUrl || input.preview;
        if (fileUrl?.startsWith('http')) {
          directImageUrls.push(fileUrl);
          continue;
        } else if (fileUrl) {
          file = await fetchFileFromUrl(fileUrl, input.fileName || 'connected.jpg');
        }
      }
      if (file) allImageFiles.push(file);
    }

    // Collect manually added images
    for (const img of images) {
      let file = img.file;
      if (!file && img.preview) {
        if (img.preview.startsWith('http')) {
          directImageUrls.push(img.preview);
        } else {
          file = await fetchFileFromUrl(img.preview, img.name || 'image.jpg');
        }
      }
      if (file) allImageFiles.push(file);
    }

    const files = allImageFiles.map((file, i) => ({ key: `image_input_${i}`, file }));
    const params = { prompt: prompt.trim() };
    directImageUrls.forEach((url, i) => {
      params[`image_input_${allImageFiles.length + i}`] = url;
    });
    if (aspectRatio) params.aspect_ratio = aspectRatio;
    if (resolution)  params.resolution  = resolution;
    if (outputFormat) params.output_format = outputFormat;
    if (googleSearch) params.google_search = true;
    if (imageSearch)  params.image_search = true;

    const usedPrompt = prompt.trim();

    const result = await processNode({
      resultType: 'banana2pro',
      files,
      params,
    });

    if (result?.outputUrl) {
      setLastPrompt(usedPrompt);
      setReconfigure(false);
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        lastPrompt: usedPrompt,
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'image',
        tool: 'banana2pro',
        filename: `banana2pro_${Date.now()}.${outputFormat || 'jpg'}`,
        url: result.outputUrl,
        prompt: usedPrompt,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, prompt, images, connectedInputs, aspectRatio, resolution, outputFormat, googleSearch, imageSearch, updateNodeData, addAsset, processNode, resetExec]);

  const handleRegenerate = useCallback(() => {
    updateNodeData(id, { hasOutput: false, outputUrl: null });    resetExec();
  }, [id, updateNodeData, resetExec]);

  // "Volver a configurar": formulario con todo lo elegido, resultado intacto.

  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  const totalImages = images.length + connectedCount;
  const canProcess = prompt.trim().length > 0;

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([textNeed(prompt)]);

  return (
    <div className={`node-container banana2pro-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`}>
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />

      {/* Single multi-input handle — accepts up to MAX_IMAGES connections */}
      <MultiInputHandle
        id={MULTI_INPUT_ID}
        nodeId={id}
        limit={MAX_IMAGES}
        label="Imágenes"
        accent="#fbbf24"
        dataTestId="banana2pro-multi-input"
      />

      {/* Header */}
      <div className="node-header banana2pro-header">
        <div className="node-header-left">
          <Banana size={16} />
          <span>Editor Banana 2 Pro</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)} data-testid="delete-banana2pro">
          <X size={14} />
        </button>
      </div>

      {/* Content */}
      <div className="node-content banana2pro-content">
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">{formatTime(chronometer)}</div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>
                {execStatus === 'uploading' ? 'Subiendo archivos...' :
                 execStatus === 'queued' ? 'En cola...' :
                 'Generando imagen...'}
              </span>
            </div>
          </div>
        ) : !showForm ? (
          /* ===== RESULT VIEW ===== */
          <div className="node-result banana2pro-result-view">
            <img
              src={data.outputUrl}
              alt="Banana 2 Pro"
              className="node-result-image clickable"
              onClick={() => openImagePreview(data.outputUrl, 'Banana 2 Pro')}
            />
            <div className="node-result-actions">
              <button className="node-mini-preview-btn" onClick={() => openImagePreview(data.outputUrl, 'Banana 2 Pro')} data-testid="banana2pro-preview-btn">
                <Eye size={12} /> Ver
              </button>
              <button className="node-mini-download-btn" onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `banana2pro_${Date.now()}.${outputFormat || 'jpg'}`); }} data-testid="banana2pro-download-btn">
                <Download size={12} /> Descargar
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="image" label="banana2pro" testIdPrefix="banana2pro-timeline" />
            </div>

            <div className="nano-regen-section">
              <span className="nano-section-label">Prompt usado:</span>
              <PromptField
                value={prompt}
                onChange={setPrompt}
                nodeId={id}
                nodeType="banana2pro"
                label="Prompt de regeneración"
                className="nano-prompt-textarea nano-regen-textarea"
                rows={2}
                testId="banana2pro-regen-prompt"
              />
              <button
                className="nano-regen-btn banana2pro-regen-btn"
                onClick={handleRegenerate}
                data-testid="banana2pro-regen-btn"
              >
                <RefreshCw size={12} />
                Regenerar
              </button>
              <ReconfigureButton
                onClick={() => setReconfigure(true)}
                testId="banana2pro-reconfigure-btn"
              />
            </div>
          </div>
        ) : (
          <>
            {data.outputUrl && reconfigure && (
              <ReconfigureNotice
                previewUrl={data.outputUrl}
                onCancel={() => setReconfigure(false)}
                testId="banana2pro-reconfigure-notice"
              />
            )}

            {connectedCount > 0 && (
              <div className="node-input-status connected">
                <CheckCircle size={12} />
                <span>{connectedCount} imagen{connectedCount > 1 ? 'es' : ''} conectada{connectedCount > 1 ? 's' : ''}</span>
              </div>
            )}

            {/* Manual images */}
            <div className="nano-images-section">
              <span className="nano-section-label">
                <ImagePlus size={12} />
                Imágenes ({totalImages}/{MAX_IMAGES}) — opcional
              </span>
              <div className="nano-images-row">
                {images.map((img, index) => (
                  <div key={img.name || `img-${index}`} className="nano-img-thumb">
                    <img src={img.preview} alt={`Ref ${index + 1}`} />
                    <button className="nano-clear-btn" onClick={() => removeImage(index)} data-testid={`banana2pro-remove-img-${index}`}>
                      <X size={8} />
                    </button>
                  </div>
                ))}
                {totalImages < MAX_IMAGES && (
                  <button className="nano-add-img-btn" onClick={handleAddImage} data-testid="banana2pro-add-image">
                    <Plus size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Prompt */}
            <div className="nano-prompt-section">
              <span className="nano-section-label">Prompt *</span>
              <PromptField
                value={prompt}
                onChange={setPrompt}
                nodeId={id}
                nodeType="banana2pro"
                placeholder="Describe lo que quieres generar o editar..."
                className="nano-prompt-textarea"
                rows={3}
                testId="banana2pro-prompt"
              />
            </div>

            {/* Options */}
            <div className="nano-options-row banana2pro-options">
              <div className="nano-option">
                <span className="nano-option-label">Ratio</span>
                <select value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)} className="nano-select" data-testid="banana2pro-aspect-ratio">
                  {ASPECT_RATIOS.map((ar) => (
                    <option key={ar.value} value={ar.value}>{ar.label}</option>
                  ))}
                </select>
              </div>
              <div className="nano-option">
                <span className="nano-option-label">Resolución</span>
                <select value={resolution} onChange={(e) => setResolution(e.target.value)} className="nano-select" data-testid="banana2pro-resolution">
                  {RESOLUTIONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div className="nano-option">
                <span className="nano-option-label">Formato</span>
                <select value={outputFormat} onChange={(e) => setOutputFormat(e.target.value)} className="nano-select" data-testid="banana2pro-output-format">
                  {OUTPUT_FORMATS.map((of_) => (
                    <option key={of_.value} value={of_.value}>{of_.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Search toggles */}
            <div className="banana2pro-toggles">
              <label className="banana2pro-toggle" title="Usar Google Web Search para contexto en tiempo real (clima, eventos, etc.)">
                <input
                  type="checkbox"
                  checked={googleSearch}
                  onChange={(e) => setGoogleSearch(e.target.checked)}
                  data-testid="banana2pro-google-search"
                />
                <Globe size={11} />
                <span>Google Web</span>
              </label>
              <label className="banana2pro-toggle" title="Usar Google Image Search para encontrar imágenes de referencia de la web">
                <input
                  type="checkbox"
                  checked={imageSearch}
                  onChange={(e) => setImageSearch(e.target.checked)}
                  data-testid="banana2pro-image-search"
                />
                <Search size={11} />
                <span>Image Search</span>
              </label>
            </div>

            <button
              className="node-process-btn banana2pro-process-btn"
              onClick={handleProcess}
              disabled={!canProcess}
              title={requirementsTitle(runMissing)}
              data-testid="banana2pro-process-btn"
            >
              <Play size={14} />
              {data.outputUrl && reconfigure
                ? 'Regenerar con cambios'
                : (totalImages > 0 ? 'Generar / Editar' : 'Generar')}
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}

        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

Banana2ProNode.displayName = 'Banana2ProNode';

export default Banana2ProNode;
